import { type Ref, computed, ref } from 'vue'

import { HEADERS } from '@backmarket/nuxt-module-http-v2/constants'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useHttpStream } from '@backmarket/nuxt-module-http-v2/useHttpStream'
import { createIdentificationUniqueId } from '@backmarket/nuxt-module-identification/createIdentificationUniqueId'

import type {
  BrowsingContextRequestBody,
  ChatRequestBody,
  MessageRequestBody,
  StreamChunk,
  StreamingEvent,
} from '~/api/shopping-assistant/types'

import { getShoppingAssistantForwardedHeaders } from '../utils/getShoppingAssistantForwardedHeaders'

/**
 * API client for the Shopping Assistant service.
 */
export function useShoppingAssistantApi() {
  const $httpFetch = useHttpFetch()
  let currentSendAbortController: AbortController | null = null
  let currentStreamAbortController: AbortController | null = null
  let currentBrowsingContextAbortController: AbortController | null = null
  const lastSendSignal: Ref<AbortSignal | null> = ref(null)

  // Builds a per-call onRequest hook: the x-request-id it reports is scoped to
  // this specific call via the closure, so a concurrent/aborting call on the
  // same composable instance can never overwrite it before the caller reads it.
  function createOnRequest(onRequestId?: (id: string | null) => void) {
    return ({ options: { headers } }: { options: { headers: Headers } }) => {
      const forwardedHeaders = getShoppingAssistantForwardedHeaders()
      Object.entries(forwardedHeaders).forEach(([key, value]) => {
        headers.set(key, value)
      })
      onRequestId?.(headers.get(HEADERS.IDENTIFICATION_REQUEST_ID))
    }
  }

  const abortSendMessage = () => {
    currentSendAbortController?.abort()
    currentStreamAbortController?.abort()
    currentStreamAbortController = null
  }

  const abortBrowsingContextPush = () => {
    currentBrowsingContextAbortController?.abort()
  }

  // Replaces the chat's live browsing context (current page). Latest call
  // wins: a new call aborts any push still in flight, so a stale response can
  // never overwrite fresher context.
  const pushBrowsingContext = async (
    chatId: string,
    body: BrowsingContextRequestBody,
    onRequestId?: (id: string | null) => void,
  ) => {
    abortBrowsingContextPush()
    const controller = new AbortController()
    currentBrowsingContextAbortController = controller

    try {
      await $httpFetch(
        '/api/shopping-agent/v1/chats/:chatId/browsing-context',
        {
          method: 'PUT',
          body,
          pathParams: { chatId },
          onRequest: createOnRequest(onRequestId),
          signal: controller.signal,
        },
      )
    } finally {
      if (currentBrowsingContextAbortController === controller) {
        currentBrowsingContextAbortController = null
      }
    }
  }

  const startChat = async (
    chatId: string | null,
    body: ChatRequestBody,
    onRequestId?: (id: string | null) => void,
  ) => {
    let responseStatus = 0
    abortSendMessage()

    const onResponse = ({ response }: { response: { status: number } }) => {
      responseStatus = response.status
    }

    if (chatId) {
      const data = await $httpFetch('/api/shopping-agent/v1/chats/:chatId', {
        method: 'GET',
        pathParams: { chatId },
        onResponse,
        onRequest: createOnRequest(onRequestId),
      })

      return { data, status: responseStatus }
    }

    const data = await $httpFetch('/api/shopping-agent/v1/chats', {
      method: 'POST',
      body,
      onResponse,
      onRequest: createOnRequest(onRequestId),
    })

    return { data, status: responseStatus }
  }

  const sendMessage = async (
    chatId: string,
    body: MessageRequestBody,
    onRequestId?: (id: string | null) => void,
  ) => {
    const controller = new AbortController()
    currentSendAbortController = controller
    lastSendSignal.value = controller.signal

    try {
      return await $httpFetch('/api/shopping-agent/v1/chats/:chatId/message', {
        method: 'POST',
        body,
        pathParams: { chatId },
        onRequest: createOnRequest(onRequestId),
        signal: controller.signal,
      })
    } finally {
      currentSendAbortController = null
    }
  }

  const streamMessage = (
    chatId: string,
    body: MessageRequestBody,
    callbacks: {
      onEvent?: (event: StreamChunk) => void
      onEnd?: (terminatedBySentinel: boolean) => void
      onAbort?: () => void
      onError?: (error: Error) => void
      onRequestId?: (id: string | null) => void
    },
  ) => {
    const $httpStream = useHttpStream()
    const controller = new AbortController()
    currentStreamAbortController = controller
    // useHttpStream has no onRequest hook to read the plugin-generated
    // x-request-id back from, so generate it ourselves and pass it
    // explicitly (it overrides the plugin's default of the same name).
    const xRequestId = createIdentificationUniqueId()
    callbacks.onRequestId?.(xRequestId)

    const streamPromise = $httpStream(
      '/api/shopping-agent/v1/chats/:chatId/message',
      {
        method: 'POST',
        pathParams: { chatId },
        headers: {
          'Content-Type': 'application/json',
          [HEADERS.IDENTIFICATION_REQUEST_ID]: xRequestId,
          ...getShoppingAssistantForwardedHeaders(),
        },
        body: body as unknown as Record<string, unknown>,
        signal: controller.signal,
        onEvent: callbacks.onEvent
          ? (event: unknown) => {
              if (controller.signal.aborted) return

              callbacks.onEvent?.(event as StreamChunk)
            }
          : undefined,
        onEnd: callbacks.onEnd,
        onAbort: callbacks.onAbort,
        onError: callbacks.onError,
      },
    )

    streamPromise.catch((err: Error) => {
      if (err.name === 'AbortError') {
        callbacks.onAbort?.()

        return
      }
      if (!callbacks.onError) return
      const match = err.message.match(/HTTP Stream Error: (\d+)/)
      callbacks.onError(
        match
          ? Object.assign(new Error(err.message), {
              status: parseInt(match[1], 10),
            })
          : err,
      )
    })

    return streamPromise
  }

  const streamStartChat = (
    chatId: string | null,
    body: ChatRequestBody,
    callbacks: {
      onEvent?: (event: StreamingEvent) => void
      onEnd?: (terminatedBySentinel: boolean) => void
      onAbort?: () => void
      onError?: (error: Error) => void
      onRequestId?: (id: string | null) => void
    },
  ) => {
    const $httpStream = useHttpStream()
    const controller = new AbortController()
    currentStreamAbortController = controller
    // useHttpStream has no onRequest hook to read the plugin-generated
    // x-request-id back from, so generate it ourselves and pass it
    // explicitly (it overrides the plugin's default of the same name).
    const xRequestId = createIdentificationUniqueId()
    callbacks.onRequestId?.(xRequestId)

    const onEvent = callbacks.onEvent
      ? (event: unknown) => {
          if (controller.signal.aborted) return

          callbacks.onEvent?.(event as StreamingEvent)
        }
      : undefined

    const streamPromise = chatId
      ? $httpStream('/api/shopping-agent/v1/chats/:chatId', {
          method: 'GET',
          pathParams: { chatId },
          headers: {
            [HEADERS.IDENTIFICATION_REQUEST_ID]: xRequestId,
            ...getShoppingAssistantForwardedHeaders(),
          },
          signal: controller.signal,
          onEvent,
          onEnd: callbacks.onEnd,
          onAbort: callbacks.onAbort,
          onError: callbacks.onError,
        })
      : $httpStream('/api/shopping-agent/v1/chats', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            [HEADERS.IDENTIFICATION_REQUEST_ID]: xRequestId,
            ...getShoppingAssistantForwardedHeaders(),
          },
          body: body as unknown as Record<string, unknown>,
          signal: controller.signal,
          onEvent,
          onEnd: callbacks.onEnd,
          onAbort: callbacks.onAbort,
          onError: callbacks.onError,
        })

    streamPromise.catch((err: Error) => {
      if (err.name === 'AbortError') {
        callbacks.onAbort?.()

        return
      }
      if (!callbacks.onError) return
      const match = err.message.match(/HTTP Stream Error: (\d+)/)
      callbacks.onError(
        match
          ? Object.assign(new Error(err.message), {
              status: parseInt(match[1], 10),
            })
          : err,
      )
    })

    return streamPromise
  }

  return {
    startChat,
    sendMessage,
    abortSendMessage,
    isSendMessageAborted: computed(
      () => lastSendSignal.value?.aborted ?? false,
    ),
    streamMessage,
    streamStartChat,
    pushBrowsingContext,
    abortBrowsingContextPush,
  }
}
